// Mock Budget Data for Access Project Management
import type {
  ProjectBudgetData,
  BudgetSummary,
  CostBudget,
  SalesBudget,
  ResourceBudget,
  AllBudgetsView,
  BudgetAlert,
  BudgetRevision,
  BurnRateDataPoint,
  BillingMilestone,
  BillingMilestoneStatus,
  RevenueStream,
} from '@/types/budget.types';
import { mockGlobalCategories, mockGlobalSubcategories } from './category-mock-data';

// ============================================================================
// PROJECT 1: FIXED PRICE - Website Redesign (Amber - Cost Warning)
// ============================================================================

const fixedPriceProject: ProjectBudgetData = {
  projectId: 1,
  projectName: 'Website Redesign',
  projectType: 'fixed-price',
  
  summary: {
    revenue: {
      budget: 100000,
      actual: 45000,
      variance: 55000,
      variancePercent: 55,
      progressPercent: 45,
      trend: 'up',
      status: 'green',
    },
    costs: {
      budget: 93500,
      actual: 47000,
      variance: 46500,
      variancePercent: 49.7,
      progressPercent: 50.3,
      trend: 'up',
      status: 'amber',
    },
    margin: {
      budgetedMargin: 40.0,
      actualMargin: 27.8,
      variance: -12.2,
      status: 'amber',
    },
    hours: {
      budget: 600,
      actual: 310,
      remaining: 290,
      progressPercent: 51.7,
      status: 'amber',
    },
    health: {
      score: 'amber',
      statusText: 'Requires Attention',
      factors: [
        'Cost budget 54% consumed with 45% of work remaining',
        'Margin has decreased from 40% to 27.8%',
        'Additional design hours impacting profitability',
      ],
    },
  },
  
  costBudget: {
    projectId: 1,
    categories: [
      {
        id: 1,
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 1,
        budget: 40000,
        actual: 23000,
        committed: 0,
        available: 17000,
        progressPercent: 57.5,
        subcategories: [
          {
            id: 101,
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 1,
            budget: 20000,
            actual: 13000,
            committed: 0,
            available: 7000,
            progressPercent: 65.0,
          },
          {
            id: 102,
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 2,
            budget: 15000,
            actual: 8000,
            committed: 0,
            available: 7000,
            progressPercent: 53.3,
          },
          {
            id: 103,
            name: 'Business Analysis',
            scope: 'global',
            globalSubcategoryId: 3,
            budget: 5000,
            actual: 2000,
            committed: 0,
            available: 3000,
            progressPercent: 40.0,
          },
        ],
      },
      {
        id: 2,
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 2,
        budget: 15000,
        actual: 8000,
        committed: 0,
        available: 7000,
        progressPercent: 53.3,
        subcategories: [
          {
            id: 104,
            name: 'Frontend Development',
            scope: 'global',
            globalSubcategoryId: 4,
            budget: 8000,
            actual: 4500,
            committed: 0,
            available: 3500,
            progressPercent: 56.3,
          },
          {
            id: 105,
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 5,
            budget: 7000,
            actual: 3500,
            committed: 0,
            available: 3500,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 3,
        name: 'Design',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 3,
        budget: 12000,
        actual: 6000,
        committed: 0,
        available: 6000,
        progressPercent: 50.0,
        subcategories: [
          {
            id: 106,
            name: 'UI Design',
            scope: 'global',
            globalSubcategoryId: 8,
            budget: 8000,
            actual: 4000,
            committed: 0,
            available: 4000,
            progressPercent: 50.0,
          },
          {
            id: 107,
            name: 'UX Research',
            scope: 'global',
            globalSubcategoryId: 9,
            budget: 4000,
            actual: 2000,
            committed: 0,
            available: 2000,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 4,
        name: 'Travel',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 6,
        budget: 8000,
        actual: 2500,
        committed: 0,
        available: 5500,
        progressPercent: 31.3,
        subcategories: [
          {
            id: 108,
            name: 'Flights',
            scope: 'global',
            globalSubcategoryId: 11,
            budget: 5000,
            actual: 1800,
            committed: 0,
            available: 3200,
            progressPercent: 36.0,
          },
          {
            id: 109,
            name: 'Ground Transport',
            scope: 'global',
            globalSubcategoryId: 12,
            budget: 3000,
            actual: 700,
            committed: 0,
            available: 2300,
            progressPercent: 23.3,
          },
        ],
      },
      {
        id: 5,
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 8,
        budget: 5000,
        actual: 2000,
        committed: 0,
        available: 3000,
        progressPercent: 40.0,
        subcategories: [
          {
            id: 110,
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 14,
            budget: 2000,
            actual: 800,
            committed: 0,
            available: 1200,
            progressPercent: 40.0,
          },
          {
            id: 111,
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 16,
            budget: 3000,
            actual: 1200,
            committed: 0,
            available: 1800,
            progressPercent: 40.0,
          },
        ],
      },
      {
        id: 6,
        name: 'Technical Subcontractors',
        type: 'subcontractors',
        scope: 'global',
        globalCategoryId: 11,
        budget: 7000,
        actual: 1500,
        committed: 3000,
        available: 2500,
        progressPercent: 64.3,
      },

      {
        id: 7,
        name: 'Custom Website Hosting',
        type: 'expenses',
        scope: 'project',
        budget: 1500,
        actual: 500,
        committed: 0,
        available: 1000,
        progressPercent: 33.3,
      },
    ],
    phases: [
      {
        id: 201,
        phaseId: 1,
        phaseName: 'Discovery',
        budget: 10000,
        actual: 9800,
        variance: 200,
        status: 'completed',
      },
      {
        id: 202,
        phaseId: 2,
        phaseName: 'Design',
        budget: 15000,
        actual: 14200,
        variance: 800,
        status: 'completed',
      },
      {
        id: 203,
        phaseId: 3,
        phaseName: 'Development',
        budget: 25000,
        actual: 8500,
        variance: 16500,
        status: 'in-progress',
      },
      {
        id: 204,
        phaseId: 4,
        phaseName: 'Testing',
        budget: 7000,
        actual: 0,
        variance: 7000,
        status: 'not-started',
      },
      {
        id: 205,
        phaseId: 5,
        phaseName: 'Deployment',
        budget: 3000,
        actual: 0,
        variance: 3000,
        status: 'not-started',
      },
    ],
    totals: {
      budget: 60000,
      actual: 32500,
      committed: 3000,
      available: 24500,
      progressPercent: 59.2,
    },
  },
  
  salesBudget: {
    projectId: 1,
    projectType: 'fixed-price',
    contractValue: 100000,
    invoiced: 60000,
    recognised: 45000,
    remaining: 55000,
    // Project 1: billingTriggerType = 'percent-complete', completionPercent = 48
    // Story: first milestone (deposit) is date-based and paid, then four % complete milestones.
    // - 25% threshold (met at 48%) with a past deadline → Overdue
    // - 50% threshold (not yet met at 48%) → Scheduled
    // - 75% threshold → Scheduled
    // - 100% threshold → Scheduled
    milestones: [
      {
        id: 301,
        name: 'Project Kick-off / Deposit',
        amount: 10000,
        dueDate: '2024-01-15',
        status: 'paid',
      },
      {
        id: 302,
        name: 'Phase 1 Completion',
        amount: 20000,
        // Deadline was 2024-10-31 — passed. Threshold IS met (48% >= 25%) → Overdue.
        dueDate: '2024-10-31',
        status: 'overdue',
        triggerPercent: 25,
      },
      {
        id: 303,
        name: 'Mid-Project Review',
        amount: 25000,
        // Optional deadline still in future
        dueDate: '2025-09-30',
        status: 'pending',
        triggerPercent: 50,
      },
      {
        id: 304,
        name: 'Phase 3 Sign-off',
        amount: 30000,
        dueDate: '2025-12-31',
        status: 'pending',
        triggerPercent: 75,
      },
      {
        id: 305,
        name: 'Final Delivery',
        amount: 15000,
        dueDate: '2026-03-31',
        status: 'pending',
        triggerPercent: 100,
      },
    ],
    revenueRecognition: {
      method: 'percentage-of-completion',
      percentComplete: 45,
      revenueToRecognise: 45000,
      revenueRecognised: 45000,
      deferredRevenue: 15000,
      unbilledRevenue: 0,
      toRecognise: 45000,
      recognised: 45000,
      deferred: 15000,
      unbilled: 0,
    },
    phases: [
      {
        id: 401,
        name: 'Discovery',
        allocated: 15000,
        recognised: 15000,
        invoiced: 15000,
        variance: 0,
      },
      {
        id: 402,
        name: 'Design',
        allocated: 25000,
        recognised: 23000,
        invoiced: 20000,
        variance: -2000,
      },
      {
        id: 403,
        name: 'Development',
        allocated: 40000,
        recognised: 7000,
        invoiced: 15000,
        variance: -33000,
      },
      {
        id: 404,
        name: 'Testing',
        allocated: 15000,
        recognised: 0,
        invoiced: 10000,
        variance: -15000,
      },
      {
        id: 405,
        name: 'Deployment',
        allocated: 5000,
        recognised: 0,
        invoiced: 0,
        variance: -5000,
      },
    ],
  },
  
  resourceBudget: {
    projectId: 1,
    summary: {
      totalBudget: 600,
      totalActual: 482, // Updated: 110 + 135 + 65 + 120 + 42 + 10 = 80.3% (WARNING)
      totalRemaining: 118,
      progressPercent: 80.3,
      billablePercent: 82.3,
      targetBillablePercent: 85.0,
      totalBudgetCost: 93500,
      totalActualCost: 70250, // Updated: 22000 + 20250 + 7800 + 15000 + 4200 + 1000
    },
    byRole: [
            {
              id: 501,
              role: 'Principal Consultant',
              budgetHours: 100,
              allocatedHours: 100,
              actualHours: 110, // OVER STATUS - 110% used
              remainingHours: -10,
              percentUsed: 110,
              billRate: 200,
              budgetCost: 20000,
              actualCost: 22000,
              monthlyHours: distributeHoursAcrossMonths(100),
            },
            {
              id: 502,
              role: 'Senior Consultant',
              budgetHours: 150,
              allocatedHours: 150,
              actualHours: 135, // WARNING STATUS - 90% used
              remainingHours: 15,
              percentUsed: 90,
              billRate: 150,
              budgetCost: 22500,
              actualCost: 20250,
              monthlyHours: distributeHoursAcrossMonths(150),
            },
            {
              id: 503,
              role: 'Consultant',
              budgetHours: 100,
              allocatedHours: 100,
              actualHours: 65, // GOOD STATUS - 65% used
              remainingHours: 35,
              percentUsed: 65,
              billRate: 120,
              budgetCost: 12000,
              actualCost: 7800,
              monthlyHours: distributeHoursAcrossMonths(100),
            },
            {
              id: 504,
              role: 'Senior Developer',
              budgetHours: 150,
              allocatedHours: 150,
              actualHours: 120, // WARNING STATUS - 80% used
              remainingHours: 30,
              percentUsed: 80,
              billRate: 125,
              budgetCost: 18750,
              actualCost: 15000,
              monthlyHours: distributeHoursAcrossMonths(150),
            },
            {
              id: 505,
              role: 'Developer',
              budgetHours: 50,
              allocatedHours: 50,
              actualHours: 42, // WARNING STATUS - 84% used
              remainingHours: 8,
              percentUsed: 84,
              billRate: 100,
              budgetCost: 5000,
              actualCost: 4200,
              monthlyHours: distributeHoursAcrossMonths(50),
            },
            {
              id: 506,
              role: 'Designer',
              budgetHours: 50,
              allocatedHours: 50,
              actualHours: 10, // GOOD STATUS - 20% used
              remainingHours: 40,
              percentUsed: 20,
              billRate: 100,
              budgetCost: 5000,
              actualCost: 1000,
              monthlyHours: distributeHoursAcrossMonths(50),
            },
    ],
    byPerson: [
      {
        id: 601,
        personId: 1,
        personName: 'Sarah Jones',
        role: 'Principal Consultant',
        budgetHours: 80,
        actualHours: 65,
        utilisation: 81.3,
        costRate: 150,
        budgetCost: 12000,
        actualCost: 9750,
      },
      {
        id: 602,
        personId: 2,
        personName: 'James Smith',
        role: 'Senior Consultant',
        budgetHours: 100,
        actualHours: 75,
        utilisation: 75.0,
        costRate: 120,
        budgetCost: 12000,
        actualCost: 9000,
      },
      {
        id: 603,
        personId: 3,
        personName: 'Emma Williams',
        role: 'Senior Consultant',
        budgetHours: 50,
        actualHours: 15,
        utilisation: 30.0,
        costRate: 120,
        budgetCost: 6000,
        actualCost: 1800,
      },
      {
        id: 604,
        personId: 4,
        personName: 'Michael Brown',
        role: 'Senior Developer',
        budgetHours: 100,
        actualHours: 50,
        utilisation: 50.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 5000,
      },
      {
        id: 605,
        personId: 5,
        personName: 'Lisa Taylor',
        role: 'Developer',
        budgetHours: 50,
        actualHours: 20,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 1600,
      },
      {
        id: 606,
        personId: 6,
        personName: 'David Wilson',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 10,
        utilisation: 20.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 800,
      },
      {
        id: 607,
        personId: 9001,
        personName: '(Unassigned)',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 4000,
      },
      {
        id: 608,
        personId: 9002,
        personName: '(Unassigned)',
        role: 'Principal Consultant',
        budgetHours: 20,
        actualHours: 15,
        utilisation: 75.0,
        costRate: 150,
        budgetCost: 3000,
        actualCost: 2250,
      },
    ],
    byPhase: [
      {
        phaseId: 1,
        phaseName: 'Discovery',
        budgetHours: 80,
        actualHours: 78,
        remainingHours: 2,
        status: 'complete',
        startDate: '2024-01-01',
        endDate: '2024-01-31',
      },
      {
        phaseId: 2,
        phaseName: 'Design',
        budgetHours: 120,
        actualHours: 110,
        remainingHours: 10,
        status: 'in-progress',
        startDate: '2024-02-01',
        endDate: '2024-03-15',
      },
      {
        phaseId: 3,
        phaseName: 'Development',
        budgetHours: 280,
        actualHours: 122,
        remainingHours: 158,
        status: 'in-progress',
        startDate: '2024-03-16',
        endDate: '2024-06-30',
      },
      {
        phaseId: 4,
        phaseName: 'Testing',
        budgetHours: 80,
        actualHours: 0,
        remainingHours: 80,
        status: 'not-started',
        startDate: '2024-07-01',
        endDate: '2024-07-31',
      },
      {
        phaseId: 5,
        phaseName: 'Deployment',
        budgetHours: 40,
        actualHours: 0,
        remainingHours: 40,
        status: 'not-started',
        startDate: '2024-08-01',
        endDate: '2024-08-15',
      },
    ],
  },
  
  allBudgets: {
    projectId: 1,
    profitability: {
      revenue: {
        budget: 100000,
        actual: 45000,
        variance: 55000,
      },
      directCosts: {
        budget: 57000,
        actual: 31000,
        variance: 26000,
      },
      grossProfit: {
        budget: 43000,
        actual: 14000,
        variance: 29000,
      },
      grossMargin: {
        budget: 43.0,
        actual: 31.1,
        variance: -11.9,
      },
      overhead: {
        budget: 0,
        actual: 0,
        variance: 0,
      },
      netProfit: {
        budget: 40000,
        actual: 12500,
        variance: 27500,
      },
      netMargin: {
        budget: 40.0,
        actual: 27.8,
        variance: -12.2,
      },
    },
    burnRateData: generateBurnRateData(60000, 32500, 0.45, 6),
    forecast: {
      projectedFinalRevenue: 98000,
      projectedFinalCost: 65000,
      projectedFinalMargin: 33.7,
      revenueVariance: -2000,
      costVariance: 5000,
      marginVariance: -6.3,
      warningMessage: 'At current rate, project will exceed cost budget by Week 10',
    },
  },
  
  alerts: [
    {
      id: 701,
      projectId: 1,
      type: 'cost-warning',
      severity: 'warning',
      title: 'Cost budget 54% consumed',
      description: 'Cost budget 54% consumed with 45% of work remaining. Labour costs tracking 12% over plan.',
      message: 'Cost budget 54% consumed with 45% of work remaining.',
      createdAt: '2024-03-15T10:00:00Z',
      dismissed: false,
    },
    {
      id: 702,
      projectId: 1,
      type: 'margin-erosion',
      severity: 'warning',
      title: 'Margin decreased to 27.8%',
      description: 'Margin has decreased from 40% to 27.8% (-12.2%). Primarily due to additional design hours.',
      message: 'Margin has decreased from 40% to 27.8% (-12.2%).',
      createdAt: '2024-03-14T14:30:00Z',
      dismissed: false,
    },
  ],
  
  revisions: [
    {
      id: 803,
      projectId: 1,
      revisionNumber: 3,
      date: '2024-03-10',
      changedBy: 'e1',
      changedByName: 'Sarah Jones',
      reason: 'Scope change CR-003 - Additional design requirements',
      status: 'current',
      costBudget: 60000,
      salesBudget: 100000,
      resourceBudget: 600,
      costChange: 5000,
      salesChange: 5000,
      resourceChange: 50,
    },
    {
      id: 802,
      projectId: 1,
      revisionNumber: 2,
      date: '2024-02-15',
      changedBy: 'e1',
      changedByName: 'Sarah Jones',
      reason: 'Phase 2 extension approved',
      status: 'baseline',
      costBudget: 55000,
      salesBudget: 95000,
      resourceBudget: 550,
      costChange: 5000,
      salesChange: 5000,
      resourceChange: 50,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-02-16',
    },
    {
      id: 801,
      projectId: 1,
      revisionNumber: 1,
      date: '2024-01-01',
      changedBy: 'e2',
      changedByName: 'James Smith',
      reason: 'Initial budget',
      status: 'baseline',
      costBudget: 50000,
      salesBudget: 90000,
      resourceBudget: 500,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-01-02',
    },
  ],
  
  lastUpdated: '2024-03-15T16:45:00Z',
};

// ============================================================================
// PROJECT 2: TIME & MATERIALS - Digital Transformation (Green - On Track)
// ============================================================================

const timeAndMaterialsProject: ProjectBudgetData = {
  projectId: 2,
  projectName: 'Digital Transformation Programme',
  projectType: 'time-and-materials',
  
  summary: {
    revenue: {
      budget: 120000,
      actual: 58000,
      variance: 62000,
      variancePercent: 51.7,
      progressPercent: 48.3,
      trend: 'up',
      status: 'green',
    },
    costs: {
      budget: 85000,
      actual: 38000,
      variance: 47000,
      variancePercent: 55.3,
      progressPercent: 44.7,
      trend: 'up',
      status: 'green',
    },
    margin: {
      budgetedMargin: 29.2,
      actualMargin: 34.5,
      variance: 5.3,
      status: 'green',
    },
    hours: {
      budget: 800,
      actual: 380,
      remaining: 420,
      progressPercent: 47.5,
      status: 'green',
    },
    health: {
      score: 'green',
      statusText: 'On Track',
      factors: [
        'Costs tracking below budget',
        'Revenue recognition on schedule',
        'Margin exceeding target by 5.3%',
      ],
    },
  },
  
  costBudget: {
    projectId: 2,
    categories: [
      {
        id: 1,
        name: 'Labour',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 100,
        budget: 65000, // Consulting (35k) + Development (25k) + Design (5k)
        actual: 30000, // Consulting (18k) + Development (10k) + Design (2k)
        committed: 0,
        available: 35000,
        progressPercent: 46.2,
        subcategories: [],
      },
      {
        id: 2,
        name: 'Materials',
        type: 'materials',
        scope: 'global',
        globalCategoryId: 101,
        budget: 8000,
        actual: 3500,
        committed: 0,
        available: 4500,
        progressPercent: 43.8,
        subcategories: [],
      },
      {
        id: 3,
        name: 'Equipment',
        type: 'equipment',
        scope: 'global',
        globalCategoryId: 102,
        budget: 6000,
        actual: 2500,
        committed: 0,
        available: 3500,
        progressPercent: 41.7,
        subcategories: [],
      },
      {
        id: 4,
        name: 'Subcontractors',
        type: 'subcontractors',
        scope: 'global',
        globalCategoryId: 11,
        budget: 5000,
        actual: 2000,
        committed: 0,
        available: 3000,
        progressPercent: 40.0,
        subcategories: [],
      },
      {
        id: 5,
        name: 'Travel',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 6,
        budget: 7000,
        actual: 3500,
        committed: 0,
        available: 3500,
        progressPercent: 50.0,
        subcategories: [
          {
            id: 106,
            name: 'Flights',
            scope: 'global',
            globalSubcategoryId: 11,
            budget: 4000,
            actual: 2000,
            committed: 0,
            available: 2000,
            progressPercent: 50.0,
          },
          {
            id: 107,
            name: 'Ground Transport',
            scope: 'global',
            globalSubcategoryId: 12,
            budget: 3000,
            actual: 1500,
            committed: 0,
            available: 1500,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 6,
        name: 'Software & Licenses',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 8,
        budget: 3500,
        actual: 1500,
        committed: 0,
        available: 2000,
        progressPercent: 42.9,
        subcategories: [],
      },
      {
        id: 7,
        name: 'Overhead',
        type: 'overhead',
        scope: 'global',
        globalCategoryId: 103,
        budget: 2500,
        actual: 1000,
        committed: 0,
        available: 1500,
        progressPercent: 40.0,
        subcategories: [],
      },
      {
        id: 8,
        name: 'Contingency',
        type: 'contingency',
        scope: 'global',
        globalCategoryId: 104,
        budget: 2000,
        actual: 0,
        committed: 0,
        available: 2000,
        progressPercent: 0,
        subcategories: [],
      },
    ],
    phases: [
      {
        id: 201,
        phaseId: 1,
        phaseName: 'Assessment',
        budget: 15000,
        actual: 14500,
        variance: 500,
        status: 'completed',
      },
      {
        id: 202,
        phaseId: 2,
        phaseName: 'Planning',
        budget: 20000,
        actual: 18000,
        variance: 2000,
        status: 'completed',
      },
      {
        id: 203,
        phaseId: 3,
        phaseName: 'Implementation',
        budget: 40000,
        actual: 5500,
        variance: 34500,
        status: 'in-progress',
      },
      {
        id: 204,
        phaseId: 4,
        phaseName: 'Optimization',
        budget: 10000,
        actual: 0,
        variance: 10000,
        status: 'not-started',
      },
    ],
    totals: {
      budget: 99000,
      actual: 44000,
      committed: 0,
      available: 55000,
      progressPercent: 44.4,
    },
  },
  
  salesBudget: {
    projectId: 2,
    projectType: 'time-and-materials',
    estimated: 120000,
    actual: 58000,
    invoiced: 52000,
    nteAmount: 150000,
    streams: [
      {
        id: 6001,
        name: 'Billable Time',
        type: 'billable-time',
        estimated: 105000,
        actual: 52000,
        invoiced: 48000,
        remaining: 53000,
        subcategories: [
          {
            id: 7001,
            name: 'Consulting',
            estimated: 60000,
            actual: 32000,
            invoiced: 30000,
            remaining: 28000,
          },
          {
            id: 7002,
            name: 'Development',
            estimated: 40000,
            actual: 18000,
            invoiced: 16000,
            remaining: 22000,
          },
          {
            id: 7003,
            name: 'Design',
            estimated: 5000,
            actual: 2000,
            invoiced: 2000,
            remaining: 3000,
          },
        ],
      },
      {
        id: 6002,
        name: 'Expense Recovery',
        type: 'expense-recovery',
        estimated: 15000,
        actual: 6000,
        invoiced: 4000,
        remaining: 9000,
      },
    ],
    unbilledTime: 4500,
    unbilledExpenses: 500,
    phaseRevenue: [
      {
        id: 401,
        phaseId: 1,
        phaseName: 'Assessment',
        budget: 22000,
        actual: 21500,
        variance: 500,
        status: 'completed',
      },
      {
        id: 402,
        phaseId: 2,
        phaseName: 'Planning',
        budget: 28000,
        actual: 27000,
        variance: 1000,
        status: 'completed',
      },
      {
        id: 403,
        phaseId: 3,
        phaseName: 'Implementation',
        budget: 55000,
        actual: 9500,
        variance: 45500,
        status: 'in-progress',
      },
      {
        id: 404,
        phaseId: 4,
        phaseName: 'Optimization',
        budget: 15000,
        actual: 0,
        variance: 15000,
        status: 'not-started',
      },
    ],
  },
  
  resourceBudget: {
    projectId: 2,
    summary: {
      totalBudget: 800,
      totalActual: 380,
      totalRemaining: 420,
      progressPercent: 47.5,
      billablePercent: 88.4,
      targetBillablePercent: 85.0,
      totalBudgetCost: 120000,
      totalActualCost: 57000,
    },
    byRole: [
          {
            id: 501,
            role: 'Principal Consultant',
            budgetHours: 150,
            allocatedHours: 150,
            actualHours: 80,
            remainingHours: 70,
            percentUsed: 53.3,
            billRate: 200,
            budgetCost: 30000,
            actualCost: 16000,
            monthlyHours: distributeHoursAcrossMonths(150),
          },
          {
            id: 502,
            role: 'Senior Consultant',
            budgetHours: 200,
            allocatedHours: 200,
            actualHours: 90,
            remainingHours: 110,
            percentUsed: 45.0,
            billRate: 150,
            budgetCost: 30000,
            actualCost: 13500,
            monthlyHours: distributeHoursAcrossMonths(200),
          },
          {
            id: 503,
            role: 'Consultant',
            budgetHours: 150,
            allocatedHours: 150,
            actualHours: 40,
            remainingHours: 110,
            percentUsed: 26.7,
            billRate: 120,
            budgetCost: 18000,
            actualCost: 4800,
            monthlyHours: distributeHoursAcrossMonths(150),
          },
          {
            id: 504,
            role: 'Developer',
            budgetHours: 250,
            allocatedHours: 250,
            actualHours: 120,
            remainingHours: 130,
            percentUsed: 48.0,
            billRate: 125,
            budgetCost: 31250,
            actualCost: 15000,
            monthlyHours: distributeHoursAcrossMonths(250),
          },
          {
            id: 505,
            role: 'Designer',
            budgetHours: 50,
            allocatedHours: 50,
            actualHours: 20,
            remainingHours: 30,
            percentUsed: 40.0,
            billRate: 100,
            budgetCost: 5000,
            actualCost: 2000,
            monthlyHours: distributeHoursAcrossMonths(50),
          },
        ],
        byPerson: [
          {
            id: 601,
            personId: 1,
            personName: 'Alice Johnson',
        role: 'Principal Consultant',
        budgetHours: 150,
        actualHours: 80,
        utilisation: 53.3,
        costRate: 150,
        budgetCost: 22500,
        actualCost: 12000,
      },
      {
        id: 602,
        personId: 3,
        personName: 'Catherine Davies',
        role: 'Senior Consultant',
        budgetHours: 120,
        actualHours: 60,
        utilisation: 50.0,
        costRate: 120,
        budgetCost: 14400,
        actualCost: 7200,
      },
      {
        id: 603,
        personId: 11,
        personName: 'Katie White',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 4000,
      },
      {
        id: 604,
        personId: 4,
        personName: 'David Smith',
        role: 'Developer',
        budgetHours: 150,
        actualHours: 80,
        utilisation: 53.3,
        costRate: 100,
        budgetCost: 15000,
        actualCost: 8000,
      },
      {
        id: 605,
        personId: 5,
        personName: 'Emma Wilson',
        role: 'Developer',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 8000,
        actualCost: 3200,
      },
      {
        id: 606,
        personId: 7,
        personName: 'Grace Taylor',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 20,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 1600,
      },
    ],
    byPhase: [
      {
        phaseId: 1,
        phaseName: 'Assessment',
        budgetHours: 120,
        actualHours: 115,
        remainingHours: 5,
        status: 'complete',
      },
      {
        phaseId: 2,
        phaseName: 'Planning',
        budgetHours: 180,
        actualHours: 170,
        remainingHours: 10,
        status: 'complete',
      },
      {
        phaseId: 3,
        phaseName: 'Implementation',
        budgetHours: 400,
        actualHours: 95,
        remainingHours: 305,
        status: 'in-progress',
      },
      {
        phaseId: 4,
        phaseName: 'Optimization',
        budgetHours: 100,
        actualHours: 0,
        remainingHours: 100,
        status: 'not-started',
      },
    ],
  },
  
  allBudgets: {
    projectId: 2,
    profitability: {
      revenue: {
        budget: 120000,
        actual: 58000,
        variance: 62000,
      },
      directCosts: {
        budget: 82000,
        actual: 37000,
        variance: 45000,
      },
      grossProfit: {
        budget: 38000,
        actual: 21000,
        variance: 17000,
      },
      grossMargin: {
        budget: 31.7,
        actual: 36.2,
        variance: 4.5,
      },
      overhead: {
        budget: 0,
        actual: 0,
        variance: 0,
      },
      netProfit: {
        budget: 35000,
        actual: 20000,
        variance: 15000,
      },
      netMargin: {
        budget: 29.2,
        actual: 34.5,
        variance: 5.3,
      },
    },
    burnRateData: generateBurnRateData(85000, 38000, 0.48, 6),
    forecast: {
      projectedFinalRevenue: 122000,
      projectedFinalCost: 82000,
      projectedFinalMargin: 32.8,
      revenueVariance: 2000,
      costVariance: -3000,
      marginVariance: 3.6,
    },
  },
  
  alerts: [],
  
  revisions: [
    {
      id: 801,
      projectId: 2,
      revisionNumber: 1,
      date: '2024-01-01',
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial budget',
      status: 'current',
      costBudget: 85000,
      salesBudget: 120000,
      resourceBudget: 800,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-01-02',
    },
  ],
  
  lastUpdated: '2024-03-15T16:45:00Z',
};

// ============================================================================
// PROJECT 3: RETAINER - Ongoing Support (Green - On Track)
// ============================================================================

const retainerProject: ProjectBudgetData = {
  projectId: 3,
  projectName: 'Ongoing Support & Maintenance',
  projectType: 'retainer',
  
  summary: {
    revenue: {
      budget: 96000,
      actual: 32350,
      variance: 63650,
      variancePercent: 66.3,
      progressPercent: 33.7,
      trend: 'stable',
      status: 'green',
    },
    costs: {
      budget: 60000,
      actual: 19500,
      variance: 40500,
      variancePercent: 67.5,
      progressPercent: 32.5,
      trend: 'stable',
      status: 'green',
    },
    margin: {
      budgetedMargin: 37.5,
      actualMargin: 39.7,
      variance: 2.2,
      status: 'green',
    },
    hours: {
      budget: 600,
      actual: 183,
      remaining: 417,
      progressPercent: 30.5,
      status: 'green',
    },
    health: {
      score: 'green',
      statusText: 'On Track',
      factors: [
        'Retainer utilisation within expected range',
        'Margin exceeding target',
        'Client satisfaction high',
      ],
    },
  },
  
  costBudget: {
    projectId: 3,
    categories: [
      {
        id: 1,
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 1,
        budget: 35000,
        actual: 12000,
        committed: 0,
        available: 23000,
        progressPercent: 34.3,
        subcategories: [
          {
            id: 101,
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 1,
            budget: 20000,
            actual: 7000,
            committed: 0,
            available: 13000,
            progressPercent: 35.0,
          },
          {
            id: 102,
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 2,
            budget: 15000,
            actual: 5000,
            committed: 0,
            available: 10000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 2,
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 2,
        budget: 12000,
        actual: 3500,
        committed: 0,
        available: 8500,
        progressPercent: 29.2,
        subcategories: [
          {
            id: 103,
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 5,
            budget: 12000,
            actual: 3500,
            committed: 0,
            available: 8500,
            progressPercent: 29.2,
          },
        ],
      },
      {
        id: 3,
        name: 'Testing & QA',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 4,
        budget: 3000,
        actual: 1000,
        committed: 0,
        available: 2000,
        progressPercent: 33.3,
        subcategories: [
          {
            id: 104,
            name: 'Automated Testing',
            scope: 'global',
            globalSubcategoryId: 10,
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 4,
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 8,
        budget: 6000,
        actual: 2000,
        committed: 0,
        available: 4000,
        progressPercent: 33.3,
        subcategories: [
          {
            id: 105,
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 14,
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
          {
            id: 106,
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 16,
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 5,
        name: 'Custom Support Tools',
        type: 'expenses',
        scope: 'project',
        budget: 2000,
        actual: 500,
        committed: 0,
        available: 1500,
        progressPercent: 25.0,
      },
    ],
    phases: [],
    totals: {
      budget: 60000,
      actual: 19500,
      committed: 0,
      available: 40500,
      progressPercent: 32.5,
    },
  },
  
  salesBudget: {
    projectId: 3,
    projectType: 'retainer',
    retainer: {
      monthlyAmount: 8000,
      contractMonths: 12,
      totalContract: 96000,
      contractValue: 96000,
      invoiced: 32000,
      remaining: 64000,
      monthsCompleted: 4,
      monthsRemaining: 8,
      currentMonth: {
        hoursIncluded: 50,
        hoursUsed: 38,
        hoursRemaining: 12,
        overageRate: 175,
        progressPercent: 76,
      },
      rolloverPolicy: 'limited',
      rolloverLimit: 10,
      monthlyBreakdown: [
        {
          month: 'Sep 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 48,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
        {
          month: 'Oct 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 52,
          overageHours: 2,
          overageAmount: 350,
          totalAmount: 8350,
        },
        {
          month: 'Nov 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 45,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
        {
          month: 'Dec 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 38,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
      ],
    },
    utilisation: {
      currentMonth: 'Dec 2024',
      includedHours: 50,
      hoursUsed: 38,
      hoursRemaining: 12,
      overageRate: 175,
      percentUsed: 76,
      rolloverPolicy: 'limited',
      rolloverHours: 10,
    },
    monthlyBreakdown: [
      {
        month: 'Sep 2024',
        retainerAmount: 8000,
        hoursIncluded: 50,
        hoursUsed: 48,
        overageAmount: 0,
        totalBilled: 8000,
      },
      {
        month: 'Oct 2024',
        retainerAmount: 8000,
        hoursIncluded: 50,
        hoursUsed: 52,
        overageAmount: 350,
        totalBilled: 8350,
      },
      {
        month: 'Nov 2024',
        retainerAmount: 8000,
        hoursIncluded: 50,
        hoursUsed: 45,
        overageAmount: 0,
        totalBilled: 8000,
      },
      {
        month: 'Dec 2024',
        retainerAmount: 8000,
        hoursIncluded: 50,
        hoursUsed: 38,
        overageAmount: 0,
        totalBilled: 8000,
      },
    ],
  },
  
  resourceBudget: {
    projectId: 3,
    summary: {
      totalBudget: 600,
      totalActual: 183,
      totalRemaining: 417,
      progressPercent: 30.5,
      billablePercent: 95.0,
      targetBillablePercent: 90.0,
      totalBudgetCost: 78000,
      totalActualCost: 23790,
    },
    byRole: [
          {
            id: 501,
            role: 'Senior Consultant',
            budgetHours: 200,
            allocatedHours: 200,
            actualHours: 65,
            remainingHours: 135,
            percentUsed: 32.5,
            billRate: 160,
            budgetCost: 32000,
            actualCost: 10400,
            monthlyHours: distributeHoursAcrossMonths(200),
          },
          {
            id: 502,
            role: 'Consultant',
            budgetHours: 250,
            allocatedHours: 250,
            actualHours: 78,
            remainingHours: 172,
            percentUsed: 31.2,
            billRate: 120,
            budgetCost: 30000,
            actualCost: 9360,
            monthlyHours: distributeHoursAcrossMonths(250),
          },
          {
            id: 503,
            role: 'Developer',
            budgetHours: 150,
            allocatedHours: 150,
            actualHours: 40,
            remainingHours: 110,
            percentUsed: 26.7,
            billRate: 125,
            budgetCost: 18750,
            actualCost: 5000,
            monthlyHours: distributeHoursAcrossMonths(150),
          },
        ],
        byPerson: [
          {
            id: 601,
            personId: 3,
            personName: 'Catherine Davies',
        role: 'Senior Consultant',
        budgetHours: 200,
        actualHours: 65,
        utilisation: 32.5,
        costRate: 120,
        budgetCost: 24000,
        actualCost: 7800,
      },
      {
        id: 602,
        personId: 6,
        personName: 'Frank Brown',
        role: 'Consultant',
        budgetHours: 150,
        actualHours: 48,
        utilisation: 32.0,
        costRate: 100,
        budgetCost: 15000,
        actualCost: 4800,
      },
      {
        id: 603,
        personId: 11,
        personName: 'Katie White',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 30,
        utilisation: 30.0,
        costRate: 80,
        budgetCost: 8000,
        actualCost: 2400,
      },
      {
        id: 604,
        personId: 12,
        personName: 'Liam Harris',
        role: 'Developer',
        budgetHours: 150,
        actualHours: 40,
        utilisation: 26.7,
        costRate: 80,
        budgetCost: 12000,
        actualCost: 3200,
      },
    ],
    byPhase: [],
  },
  
  allBudgets: {
    projectId: 3,
    profitability: {
      revenue: {
        budget: 96000,
        actual: 32350,
        variance: 63650,
      },
      directCosts: {
        budget: 58000,
        actual: 19000,
        variance: 39000,
      },
      grossProfit: {
        budget: 38000,
        actual: 13350,
        variance: 24650,
      },
      grossMargin: {
        budget: 39.6,
        actual: 41.3,
        variance: 1.7,
      },
      overhead: {
        budget: 0,
        actual: 0,
        variance: 0,
      },
      netProfit: {
        budget: 36000,
        actual: 12850,
        variance: 23150,
      },
      netMargin: {
        budget: 37.5,
        actual: 39.7,
        variance: 2.2,
      },
    },
    burnRateData: generateBurnRateData(60000, 19500, 0.33, 4),
    forecast: {
      projectedFinalRevenue: 96000,
      projectedFinalCost: 58500,
      projectedFinalMargin: 39.1,
      revenueVariance: 0,
      costVariance: -1500,
      marginVariance: 1.6,
    },
  },
  
  alerts: [],
  
  revisions: [
    {
      id: 801,
      projectId: 3,
      revisionNumber: 1,
      date: '2024-09-01',
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial retainer agreement',
      status: 'current',
      costBudget: 60000,
      salesBudget: 96000,
      resourceBudget: 600,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-09-02',
    },
  ],
  
  lastUpdated: '2024-12-15T16:45:00Z',
};

// ============================================================================
// PROJECT 4: FIXED PRICE - Legal Case Management System (Amber - Cost Warning)
// ============================================================================

const legalCaseManagementProject: ProjectBudgetData = {
  projectId: 4,
  projectName: 'Legal Case Management System',
  projectType: 'fixed-price',
  
  summary: {
    revenue: {
      budget: 380000,
      actual: 190000,
      variance: 190000,
      variancePercent: 50,
      progressPercent: 50,
      trend: 'up',
      status: 'green',
    },
    costs: {
      budget: 300000,
      actual: 213000,
      variance: 87000,
      variancePercent: 29,
      progressPercent: 71,
      trend: 'up',
      status: 'amber',
    },
    margin: {
      budgetedMargin: 21.1,
      actualMargin: -12.1,
      variance: -33.2,
      status: 'red',
    },
    hours: {
      budget: 5100,
      actual: 3550,
      remaining: 1550,
      progressPercent: 69.6,
      status: 'amber',
    },
    health: {
      score: 'amber',
      statusText: 'Requires Attention',
      factors: [
        'Cost tracking ahead of revenue recognition',
        'Margin declining due to scope creep',
        'Hours utilisation higher than planned',
      ],
    },
  },
  
  costBudget: {
    projectId: 4,
    categories: [
      {
        id: 4001,
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 1,
        budget: 120000,
        actual: 85000,
        committed: 15000,
        available: 20000,
        progressPercent: 70.8,
        subcategories: [
          {
            id: 40011,
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 1,
            budget: 60000,
            actual: 42000,
            committed: 8000,
            available: 10000,
            progressPercent: 70.0,
          },
          {
            id: 40012,
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 2,
            budget: 60000,
            actual: 43000,
            committed: 7000,
            available: 10000,
            progressPercent: 71.7,
          },
        ],
      },
      {
        id: 4002,
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 2,
        budget: 140000,
        actual: 105000,
        committed: 18000,
        available: 17000,
        progressPercent: 75.0,
        subcategories: [
          {
            id: 40021,
            name: 'Frontend Development',
            scope: 'global',
            globalSubcategoryId: 4,
            budget: 50000,
            actual: 38000,
            committed: 6000,
            available: 6000,
            progressPercent: 76.0,
          },
          {
            id: 40022,
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 5,
            budget: 70000,
            actual: 52000,
            committed: 9000,
            available: 9000,
            progressPercent: 74.3,
          },
          {
            id: 40023,
            name: 'Database Development',
            scope: 'global',
            globalSubcategoryId: 6,
            budget: 20000,
            actual: 15000,
            committed: 3000,
            available: 2000,
            progressPercent: 75.0,
          },
        ],
      },
      {
        id: 4003,
        name: 'Testing & QA',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 4,
        budget: 20000,
        actual: 12000,
        committed: 4000,
        available: 4000,
        progressPercent: 60.0,
        subcategories: [
          {
            id: 40031,
            name: 'Manual Testing',
            scope: 'global',
            globalSubcategoryId: 9,
            budget: 12000,
            actual: 7000,
            committed: 2500,
            available: 2500,
            progressPercent: 58.3,
          },
          {
            id: 40032,
            name: 'Automated Testing',
            scope: 'global',
            globalSubcategoryId: 10,
            budget: 8000,
            actual: 5000,
            committed: 1500,
            available: 1500,
            progressPercent: 62.5,
          },
        ],
      },
      {
        id: 4004,
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 8,
        budget: 15000,
        actual: 9000,
        committed: 3000,
        available: 3000,
        progressPercent: 60.0,
        subcategories: [
          {
            id: 40041,
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 14,
            budget: 8000,
            actual: 5000,
            committed: 1500,
            available: 1500,
            progressPercent: 62.5,
          },
          {
            id: 40042,
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 16,
            budget: 7000,
            actual: 4000,
            committed: 1500,
            available: 1500,
            progressPercent: 57.1,
          },
        ],
      },
      {
        id: 4005,
        name: 'Legal Compliance',
        type: 'expenses',
        scope: 'project',
        budget: 5000,
        actual: 2000,
        committed: 1000,
        available: 2000,
        progressPercent: 40.0,
      },
    ],
    phases: [
      {
        id: 4101,
        phaseId: 4101,
        phaseName: 'Discovery & Planning',
        name: 'Discovery & Planning',
        budget: 60000,
        actual: 58000,
        variance: 2000,
        status: 'completed',
      },
      {
        id: 4102,
        phaseId: 4102,
        phaseName: 'Design & Architecture',
        name: 'Design & Architecture',
        budget: 80000,
        actual: 75000,
        variance: 5000,
        status: 'completed',
      },
      {
        id: 4103,
        phaseId: 4103,
        phaseName: 'Development',
        name: 'Development',
        budget: 120000,
        actual: 65000,
        variance: 55000,
        status: 'in-progress',
      },
      {
        id: 4104,
        phaseId: 4104,
        phaseName: 'Testing & UAT',
        name: 'Testing & UAT',
        budget: 30000,
        actual: 15000,
        variance: 15000,
        status: 'in-progress',
      },
      {
        id: 4105,
        phaseId: 4105,
        phaseName: 'Deployment & Training',
        name: 'Deployment & Training',
        budget: 10000,
        actual: 0,
        variance: 10000,
        status: 'not-started',
      },
    ],
    totals: {
      budget: 300000,
      actual: 213000,
      committed: 40000,
      available: 47000,
      progressPercent: 71.0,
    },
  },
  
  salesBudget: {
    projectId: 4,
    projectType: 'fixed-price',
    contractValue: 380000,
    milestones: [
      // Project 4: billingTriggerType = 'percent-complete', completionPercent = 58
      // Story: deposit paid, 25% threshold met + deadline passed → Overdue,
      // 50% threshold met (58% >= 50%) → Due (ready to invoice),
      // 75% threshold not met → Scheduled, 100% → Scheduled.
      {
        id: 4201,
        name: 'Contract Signing / Deposit',
        amount: 76000,
        dueDate: '2024-10-01',
        status: 'paid',
      },
      {
        id: 4202,
        name: 'Phase 1 Completion',
        amount: 76000,
        // Deadline passed — threshold was met — Overdue
        dueDate: '2025-01-31',
        status: 'overdue',
        triggerPercent: 25,
      },
      {
        id: 4203,
        name: 'Mid-Project Review',
        amount: 95000,
        // Optional future deadline — threshold IS met (58% >= 50%) → Due
        dueDate: '2025-09-30',
        status: 'due',
        triggerPercent: 50,
      },
      {
        id: 4204,
        name: 'Phase 3 Sign-off',
        amount: 76000,
        dueDate: '2025-12-31',
        status: 'pending',
        triggerPercent: 75,
      },
      {
        id: 4205,
        name: 'Final Delivery',
        amount: 57000,
        dueDate: '2026-04-30',
        status: 'pending',
        triggerPercent: 100,
      },
    ],
    revenueRecognition: {
      method: 'percentage-of-completion',
      percentComplete: 58,
      revenueToRecognise: 220400,
      revenueRecognised: 190000,
      deferredRevenue: 38000,
      unbilledRevenue: 23000,
      toRecognise: 220400,
      recognised: 190000,
      deferred: 38000,
      unbilled: 23000,
    },
    phases: [
      {
        id: 4101,
        name: 'Discovery & Planning',
        allocated: 60000,
        recognised: 60000,
        invoiced: 76000,
        variance: 0,
      },
      {
        id: 4102,
        name: 'Design & Architecture',
        allocated: 80000,
        recognised: 76000,
        invoiced: 76000,
        variance: -4000,
      },
      {
        id: 4103,
        name: 'Development',
        allocated: 180000,
        recognised: 54000,
        invoiced: 38000,
        variance: -126000,
      },
      {
        id: 4104,
        name: 'Testing & UAT',
        allocated: 40000,
        recognised: 0,
        invoiced: 0,
        variance: -40000,
      },
      {
        id: 4105,
        name: 'Deployment & Training',
        allocated: 20000,
        recognised: 0,
        invoiced: 0,
        variance: -20000,
      },
    ],
  },
  
  resourceBudget: {
    projectId: 4,
    summary: {
      totalBudget: 5100,
      totalActual: 3550,
      totalRemaining: 1550,
      progressPercent: 69.6,
      billablePercent: 86,
      targetBillablePercent: 85,
      totalBudgetCost: 300000,
      totalActualCost: 210100,
    },
    byRole: [
      {
        id: 4301,
        role: 'Project Manager',
        budgetHours: 400,
        allocatedHours: 400,
        actualHours: 285,
        remainingHours: 115,
        percentUsed: 71.3,
        billRate: 100,
        budgetCost: 40000,
        actualCost: 28500,
      },
      {
        id: 4302,
        role: 'Consultant',
        budgetHours: 1200,
        allocatedHours: 1200,
        actualHours: 850,
        remainingHours: 350,
        percentUsed: 70.8,
        billRate: 100,
        budgetCost: 120000,
        actualCost: 85000,
      },
      {
        id: 4303,
        role: 'Developer',
        budgetHours: 2800,
        allocatedHours: 2800,
        actualHours: 1980,
        remainingHours: 820,
        percentUsed: 70.7,
        billRate: 40,
        budgetCost: 112000,
        actualCost: 79200,
      },
      {
        id: 4304,
        role: 'QA Tester',
        budgetHours: 500,
        allocatedHours: 500,
        actualHours: 300,
        remainingHours: 200,
        percentUsed: 60.0,
        billRate: 40,
        budgetCost: 20000,
        actualCost: 12000,
      },
      {
        id: 4305,
        role: 'UX Designer',
        budgetHours: 200,
        allocatedHours: 200,
        actualHours: 135,
        remainingHours: 65,
        percentUsed: 67.5,
        billRate: 40,
        budgetCost: 8000,
        actualCost: 5400,
      },
    ],
    byPerson: [],
    byPhase: [],
  },
  
  allBudgets: {
    projectId: 4,
    profitability: {
      revenue: { budget: 380000, actual: 190000, variance: -190000 },
      directCosts: { budget: 300000, actual: 213000, variance: 87000 },
      grossProfit: { budget: 80000, actual: -23000, variance: -103000 },
      grossMargin: { budget: 21.1, actual: -12.1, variance: -33.2 },
      overhead: { budget: 20000, actual: 15000, variance: -5000 },
      netProfit: { budget: 60000, actual: -38000, variance: -98000 },
      netMargin: { budget: 15.8, actual: -20.0, variance: -35.8 },
    },
    burnRateData: [
      { date: '2024-10-01', budgetCumulative: 60000, actualCumulative: 58000, forecastCumulative: 58000 },
      { date: '2024-11-01', budgetCumulative: 140000, actualCumulative: 133000, forecastCumulative: 135000 },
      { date: '2024-12-01', budgetCumulative: 180000, actualCumulative: 175000, forecastCumulative: 180000 },
      { date: '2025-01-01', budgetCumulative: 220000, actualCumulative: 200000, forecastCumulative: 210000 },
      { date: '2025-02-01', budgetCumulative: 260000, actualCumulative: 213000, forecastCumulative: 240000 },
    ],
    forecast: {
      projectedFinalRevenue: 350000,
      projectedFinalCost: 320000,
      projectedFinalMargin: 8.6,
      revenueVariance: -30000,
      costVariance: 20000,
      marginVariance: -12.5,
      warningMessage: 'Project at risk of cost overrun',
    },
  },
  
  alerts: [
    {
      id: 4401,
      projectId: 4,
      type: 'cost-over-budget',
      severity: 'warning',
      title: 'Cost Tracking Ahead',
      description: 'Cost tracking 71% of budget with 58% completion',
      message: 'Cost tracking 71% of budget with 58% completion',
      createdAt: new Date().toISOString(),
      dismissed: false,
    },
    {
      id: 4402,
      projectId: 4,
      type: 'margin-erosion',
      severity: 'critical',
      title: 'Margin Declining',
      description: 'Margin has declined from 21.1% to -12.1%',
      message: 'Margin has declined from 21.1% to -12.1%',
      createdAt: new Date().toISOString(),
      dismissed: false,
    },
  ],
  
  revisions: [
    {
      id: 4501,
      projectId: 4,
      revisionNumber: 1,
      date: '2024-09-15',
      changedBy: 'e2',
      changedByName: 'Bob Smith',
      reason: 'Initial budget approval',
      status: 'approved',
      costBudget: 300000,
      salesBudget: 380000,
      resourceBudget: 5100,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-09-16',
    },
  ],
  
  lastUpdated: '2025-02-03T17:30:00Z',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Distributes total hours equally across 12 months
 * Returns an array of 12 numbers representing monthly hours
 */
function distributeHoursAcrossMonths(totalHours: number): number[] {
  const monthlyHours = totalHours / 12;
  // Round to 1 decimal place for cleaner display
  const roundedMonthly = Math.round(monthlyHours * 10) / 10;
  
  // Create array of 12 months with equal distribution
  const months = Array(12).fill(roundedMonthly);
  
  // Adjust the last month to ensure total adds up exactly
  const currentTotal = roundedMonthly * 12;
  const difference = totalHours - currentTotal;
  months[11] = Math.round((roundedMonthly + difference) * 10) / 10;
  
  return months;
}

function generateBurnRateData(
  totalBudget: number,
  currentActual: number,
  progressPercent: number,
  monthsElapsed: number
): BurnRateDataPoint[] {
  const data: BurnRateDataPoint[] = [];
  const totalMonths = Math.ceil(monthsElapsed / progressPercent);
  const monthlyBudget = totalBudget / totalMonths;
  const monthlyActual = currentActual / monthsElapsed;
  
  for (let i = 0; i <= totalMonths; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - monthsElapsed + i);
    
    data.push({
      date: date.toISOString().split('T')[0],
      budgetCumulative: Math.min(monthlyBudget * i, totalBudget),
      actualCumulative: i <= monthsElapsed ? monthlyActual * i : 0,
      forecastCumulative: i <= monthsElapsed ? 0 : monthlyActual * i,
    });
  }
  
  return data;
}

// ============================================================================
// DYNAMIC BUDGET GENERATION FOR ALL PROJECTS
// ============================================================================

// Import project data to generate realistic budgets
import { mockProjects } from '../mock-data';

function generateBudgetForProject(project: any): ProjectBudgetData {
  const totalBudget = project.budgetLabour + project.budgetExpense;
  const totalActual = project.actualLabour + project.actualExpense;
  const progress = project.completionPercent / 100;
  
  // Calculate budget metrics
  const budgetedMargin = ((project.budgetRevenue - totalBudget) / project.budgetRevenue) * 100;
  const actualMargin = project.actualRevenue > 0 ? ((project.actualRevenue - totalActual) / project.actualRevenue) * 100 : 0;
  const marginVariance = actualMargin - budgetedMargin;
  
  // Determine status based on health
  const getStatus = (health: string, variance: number) => {
    if (health === 'red' || variance > 15) return 'red';
    if (health === 'amber' || variance > 5) return 'amber';
    return 'green';
  };
  
  // Generate cost budget categories based on new flat structure
  const labourBudget = project.budgetLabour;
  const labourActual = project.actualLabour;
  
  const materialsBudget = project.budgetExpense * 0.25;
  const materialsActual = project.actualExpense * 0.24;
  const equipmentBudget = project.budgetExpense * 0.20;
  const equipmentActual = project.actualExpense * 0.21;
  const subcontractorsBudget = project.budgetExpense * 0.15;
  const subcontractorsActual = project.actualExpense * 0.16;
  const travelBudget = project.budgetExpense * 0.15;
  const travelActual = project.actualExpense * 0.14;
  const softwareBudget = project.budgetExpense * 0.15;
  const softwareActual = project.actualExpense * 0.15;
  const overheadBudget = project.budgetExpense * 0.07;
  const overheadActual = project.actualExpense * 0.07;
  const contingencyBudget = project.budgetExpense * 0.03;
  const contingencyActual = project.actualExpense * 0.03;
  
  const costBudget: CostBudget = {
    projectId: project.id,
    categories: [
      {
        id: 1,
        name: 'Labour',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 1,
        budget: labourBudget,
        actual: labourActual,
        committed: 0,
        available: labourBudget - labourActual,
        progressPercent: labourBudget > 0 ? (labourActual / labourBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 2,
        name: 'Materials',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 2,
        budget: materialsBudget,
        actual: materialsActual,
        committed: 0,
        available: materialsBudget - materialsActual,
        progressPercent: materialsBudget > 0 ? (materialsActual / materialsBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 3,
        name: 'Equipment',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 3,
        budget: equipmentBudget,
        actual: equipmentActual,
        committed: 0,
        available: equipmentBudget - equipmentActual,
        progressPercent: equipmentBudget > 0 ? (equipmentActual / equipmentBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 4,
        name: 'Subcontractors',
        type: 'subcontractors',
        scope: 'global',
        globalCategoryId: 4,
        budget: subcontractorsBudget,
        actual: subcontractorsActual,
        committed: 0,
        available: subcontractorsBudget - subcontractorsActual,
        progressPercent: subcontractorsBudget > 0 ? (subcontractorsActual / subcontractorsBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 5,
        name: 'Travel & Expenses',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 5,
        budget: travelBudget,
        actual: travelActual,
        committed: 0,
        available: travelBudget - travelActual,
        progressPercent: travelBudget > 0 ? (travelActual / travelBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 6,
        name: 'Software & Licenses',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 6,
        budget: softwareBudget,
        actual: softwareActual,
        committed: 0,
        available: softwareBudget - softwareActual,
        progressPercent: softwareBudget > 0 ? (softwareActual / softwareBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 7,
        name: 'Overhead',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 7,
        budget: overheadBudget,
        actual: overheadActual,
        committed: 0,
        available: overheadBudget - overheadActual,
        progressPercent: overheadBudget > 0 ? (overheadActual / overheadBudget) * 100 : 0,
        subcategories: [],
      },
      {
        id: 8,
        name: 'Contingency',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 8,
        budget: contingencyBudget,
        actual: contingencyActual,
        committed: 0,
        available: contingencyBudget - contingencyActual,
        progressPercent: contingencyBudget > 0 ? (contingencyActual / contingencyBudget) * 100 : 0,
        subcategories: [],
      },
    ],
    phases: [],
    totals: {
      budget: totalBudget,
      actual: totalActual,
      committed: 0,
      available: totalBudget - totalActual,
      progressPercent: (totalActual / totalBudget) * 100,
    },
  };
  
  // Generate sales budget (for all project types)
  let salesBudget: SalesBudget;
  if (project.type === 'fixed-price') {
    const invoiced = project.actualRevenue - (project.unbilledAmount || 0);
    const recognised = project.actualRevenue * 0.75;
    const paid = recognised * 0.65; // Assume 65% of recognised revenue is paid
    
    // Generate milestones per task 9.3:
    //   Even-id Fixed Fee projects → billingTriggerType: 'percent-complete'
    //     Milestone 1: date-based deposit (paid on kickoff).
    //     Milestones 2–5: triggerPercent at 25 / 50 / 75 / 100 %.
    //     Deadline assignment for % complete milestones:
    //       - First met-but-uninvoiced threshold → past deadline (→ Overdue)
    //       - Subsequent met thresholds → future deadline (→ Due)
    //       - Unmet thresholds → further future deadlines (→ Scheduled)
    //   Odd-id Fixed Fee projects → billingTriggerType: 'date'
    //     All 5 milestones are date-based (unchanged behaviour).
    const usesPercentComplete = project.id % 2 === 0;
    const numMilestones = 5;
    const milestones: BillingMilestone[] = [];

    // % complete thresholds for milestones 2–5 on even-id projects
    const percentTriggers = [25, 50, 75, 100];

    if (usesPercentComplete) {
      // ── % Complete projects (even IDs) ──────────────────────────────────────

      // Milestone 1: date-based deposit (always paid/invoiced for active projects)
      const depositValue = project.budgetRevenue * 0.10; // ~10% as deposit
      const depositStatus: BillingMilestoneStatus = progress > 0 ? 'paid' : 'pending';
      const depositDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000); // 90 days ago (already kicked off)
      milestones.push({
        id: project.id * 100 + 1,
        name: 'Project Kick-off / Deposit',
        amount: depositValue,
        dueDate: depositDate.toISOString().split('T')[0],
        status: depositStatus,
      });

      // Milestones 2–5: % complete triggered
      // Track the first met-but-uninvoiced threshold to assign a past deadline (Overdue)
      let firstMetUnpaidIndex = -1;
      const pctMilestoneValue = (project.budgetRevenue - depositValue) / percentTriggers.length;

      for (let i = 0; i < percentTriggers.length; i++) {
        const triggerPercent = percentTriggers[i]; // 25 | 50 | 75 | 100
        const thresholdMet = (progress * 100) >= triggerPercent;
        // Paid heuristic: first floor(progress * 0.65 * 4) % complete milestones are paid
        const isPaid = i < Math.floor((progress * 0.65) * percentTriggers.length);

        if (thresholdMet && !isPaid && firstMetUnpaidIndex === -1) {
          firstMetUnpaidIndex = i;
        }

        // Status reflects % complete logic:
        //   paid    → threshold passed long ago and invoice settled
        //   due     → threshold met, not yet invoiced (will be Overdue or Due depending on deadline)
        //   pending → threshold not yet reached
        const pctStatus: BillingMilestoneStatus = isPaid
          ? 'paid'
          : thresholdMet
          ? 'due'
          : 'pending';

        // Deadline assignment:
        //   First met-but-unpaid threshold → past deadline (Overdue state in billing tab)
        //   Subsequent met thresholds → future deadline (Due state)
        //   Unmet thresholds → progressively further future deadlines (Scheduled)
        let deadlineDate: string;
        if (thresholdMet && !isPaid && i === firstMetUnpaidIndex) {
          // Past deadline → Overdue
          const daysAgo = 30 + (project.id % 30); // 30–59 days in the past
          const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
          deadlineDate = d.toISOString().split('T')[0];
        } else if (thresholdMet && !isPaid) {
          // Future deadline → Due
          const daysAhead = (i + 1) * 45;
          const d = new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000);
          deadlineDate = d.toISOString().split('T')[0];
        } else {
          // Future deadline → Scheduled (or paid, deadline irrelevant)
          const baseOffset = Math.max(0, i - (firstMetUnpaidIndex === -1 ? 0 : firstMetUnpaidIndex));
          const daysAhead = 60 + baseOffset * 60;
          const d = new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000);
          deadlineDate = d.toISOString().split('T')[0];
        }

        const pctMilestoneNames: Record<number, string> = {
          25: 'Phase 1 Completion',
          50: 'Mid-Project Review',
          75: 'Phase 3 Sign-off',
          100: 'Final Delivery',
        };
        const milestoneName = pctMilestoneNames[triggerPercent] ?? `Milestone at ${triggerPercent}%`;

        milestones.push({
          id: project.id * 100 + i + 2, // +2 because deposit is id+1
          name: milestoneName,
          amount: pctMilestoneValue,
          dueDate: deadlineDate,
          status: pctStatus,
          triggerPercent,
        });
      }
    } else {
      // ── Date-based projects (odd IDs) ────────────────────────────────────────
      // All milestones are date-based — unchanged behaviour from original generator.
      for (let i = 0; i < numMilestones; i++) {
        const milestoneValue = project.budgetRevenue / numMilestones;
        const isInvoiced = i < Math.floor(progress * numMilestones);
        const isPaid = i < Math.floor((progress * 0.65) * numMilestones);
        const milestoneStatus: BillingMilestoneStatus = isPaid ? 'paid' : isInvoiced ? 'due' : 'pending';

        milestones.push({
          id: project.id * 100 + i + 1,
          name: i === 0 ? 'Project Kick-off / Deposit' : `Milestone ${i + 1}`,
          amount: milestoneValue,
          dueDate: new Date(Date.now() + (i - Math.floor(progress * numMilestones)) * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          status: milestoneStatus,
        });
      }
    }
    
    // Generate phases
    const phases = [
      {
        id: project.id * 10 + 1,
        name: 'Discovery & Planning',
        allocated: project.budgetRevenue * 0.15,
        recognised: recognised * 0.20,
        invoiced: invoiced * 0.18,
        variance: (recognised * 0.20) - (project.budgetRevenue * 0.15),
      },
      {
        id: project.id * 10 + 2,
        name: 'Design',
        allocated: project.budgetRevenue * 0.20,
        recognised: recognised * 0.25,
        invoiced: invoiced * 0.22,
        variance: (recognised * 0.25) - (project.budgetRevenue * 0.20),
      },
      {
        id: project.id * 10 + 3,
        name: 'Development',
        allocated: project.budgetRevenue * 0.40,
        recognised: recognised * 0.35,
        invoiced: invoiced * 0.38,
        variance: (recognised * 0.35) - (project.budgetRevenue * 0.40),
      },
      {
        id: project.id * 10 + 4,
        name: 'Testing',
        allocated: project.budgetRevenue * 0.15,
        recognised: recognised * 0.12,
        invoiced: invoiced * 0.15,
        variance: (recognised * 0.12) - (project.budgetRevenue * 0.15),
      },
      {
        id: project.id * 10 + 5,
        name: 'Deployment',
        allocated: project.budgetRevenue * 0.10,
        recognised: recognised * 0.08,
        invoiced: invoiced * 0.07,
        variance: (recognised * 0.08) - (project.budgetRevenue * 0.10),
      },
    ];
    
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      contractValue: project.budgetRevenue,
      invoiced,
      recognised,
      remaining: project.budgetRevenue - invoiced,
      milestones,
      revenueRecognition: {
        method: 'percentage-of-completion',
        percentComplete: progress * 100,
        revenueToRecognise: project.budgetRevenue * progress,
        revenueRecognised: recognised,
        deferredRevenue: invoiced - recognised,
        unbilledRevenue: project.unbilledAmount || 0,
        toRecognise: project.budgetRevenue * progress,
        recognised,
        deferred: invoiced - recognised,
        unbilled: project.unbilledAmount || 0,
      },
      phases,
    };
  } else if (project.type === 'time-and-materials') {
    // Generate revenue streams for T&M projects
    const billableTimeEstimated = project.budgetRevenue * 0.85; // 85% from billable time
    const billableTimeActual = project.actualRevenue * 0.87;
    const billableTimeInvoiced = billableTimeActual * 0.92; // 92% invoiced
    
    const expenseRecoveryEstimated = project.budgetRevenue * 0.15; // 15% from expense recovery
    const expenseRecoveryActual = project.actualRevenue * 0.13;
    const expenseRecoveryInvoiced = expenseRecoveryActual * 0.67; // 67% invoiced
    
    const streams: RevenueStream[] = [
      {
        id: project.id * 1000 + 1,
        name: 'Billable Time',
        type: 'billable-time',
        estimated: billableTimeEstimated,
        actual: billableTimeActual,
        invoiced: billableTimeInvoiced,
        remaining: billableTimeEstimated - billableTimeActual,
        subcategories: [
          {
            id: project.id * 1000 + 101,
            name: 'Consulting',
            estimated: billableTimeEstimated * 0.50,
            actual: billableTimeActual * 0.52,
            invoiced: billableTimeInvoiced * 0.53,
            remaining: billableTimeEstimated * 0.50 - billableTimeActual * 0.52,
          },
          {
            id: project.id * 1000 + 102,
            name: 'Development',
            estimated: billableTimeEstimated * 0.40,
            actual: billableTimeActual * 0.38,
            invoiced: billableTimeInvoiced * 0.37,
            remaining: billableTimeEstimated * 0.40 - billableTimeActual * 0.38,
          },
          {
            id: project.id * 1000 + 103,
            name: 'Design',
            estimated: billableTimeEstimated * 0.10,
            actual: billableTimeActual * 0.10,
            invoiced: billableTimeInvoiced * 0.10,
            remaining: billableTimeEstimated * 0.10 - billableTimeActual * 0.10,
          },
        ],
      },
      {
        id: project.id * 1000 + 2,
        name: 'Expense Recovery',
        type: 'expense-recovery',
        estimated: expenseRecoveryEstimated,
        actual: expenseRecoveryActual,
        invoiced: expenseRecoveryInvoiced,
        remaining: expenseRecoveryEstimated - expenseRecoveryActual,
      },
    ];
    
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      estimated: project.budgetRevenue,
      actual: project.actualRevenue,
      nteAmount: project.budgetRevenue * 1.25, // NTE is 125% of estimated
      unbilledTime: (billableTimeActual - billableTimeInvoiced) * 0.8, // 80% of unbilled is time
      unbilledExpenses: (expenseRecoveryActual - expenseRecoveryInvoiced), // Rest is expenses
      streams,
      phaseRevenue: [],
    };
  } else if (project.type === 'retainer') {
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      retainer: {
        monthlyAmount: project.budgetRevenue / 12,
        contractMonths: 12,
        totalContract: project.budgetRevenue,
        invoiced: project.actualRevenue,
        remaining: project.budgetRevenue - project.actualRevenue,
        currentMonth: {
          hoursIncluded: 40,
          hoursUsed: 32,
          hoursRemaining: 8,
          overageRate: 150,
          progressPercent: 80,
        },
        rolloverPolicy: 'none',
        monthlyBreakdown: [],
      },
      phaseRevenue: [],
    };
  } else {
    // Default fallback for any other project type
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      estimated: project.budgetRevenue,
      actual: project.actualRevenue,
      phaseRevenue: [],
    };
  }
  
  // Generate resource budget with realistic rates and calculations
  const pmBudgetHours = project.budgetHours * 0.15;
  const pmActualHours = project.actualHours * 0.14;
  const pmRate = 200;
  
  const consultantBudgetHours = project.budgetHours * 0.25;
  const consultantActualHours = project.actualHours * 0.26;
  const consultantRate = 180;
  
  const developerBudgetHours = project.budgetHours * 0.40;
  const developerActualHours = project.actualHours * 0.42;
  const developerRate = 150;
  
  const qaBudgetHours = project.budgetHours * 0.20;
  const qaActualHours = project.actualHours * 0.18;
  const qaRate = 125;
  
  // Calculate total budget and actual costs
  const totalBudgetCost = (pmBudgetHours * pmRate) + (consultantBudgetHours * consultantRate) + 
                          (developerBudgetHours * developerRate) + (qaBudgetHours * qaRate);
  const totalActualCost = (pmActualHours * pmRate) + (consultantActualHours * consultantRate) + 
                          (developerActualHours * developerRate) + (qaActualHours * qaRate);
  
  const resourceBudget: ResourceBudget = {
    projectId: project.id,
    summary: {
      totalBudget: project.budgetHours,
      totalActual: project.actualHours,
      totalRemaining: project.budgetHours - project.actualHours,
      progressPercent: (project.actualHours / project.budgetHours) * 100,
      billablePercent: 85,
      targetBillablePercent: 80,
      totalBudgetCost: totalBudgetCost,
      totalActualCost: totalActualCost,
    },
    byRole: [
      {
        id: 501,
        role: 'Project Manager',
        budgetHours: pmBudgetHours,
        allocatedHours: 0, // Will be calculated from team members
        actualHours: pmActualHours,
        remainingHours: pmBudgetHours - pmActualHours,
        percentUsed: (pmActualHours / pmBudgetHours) * 100,
        billRate: pmRate,
        budgetCost: pmBudgetHours * pmRate,
        actualCost: pmActualHours * pmRate,
        monthlyHours: distributeHoursAcrossMonths(pmBudgetHours),
      },
      {
        id: 502,
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours,
        allocatedHours: 0, // Will be calculated from team members
        actualHours: consultantActualHours,
        remainingHours: consultantBudgetHours - consultantActualHours,
        percentUsed: (consultantActualHours / consultantBudgetHours) * 100,
        billRate: consultantRate,
        budgetCost: consultantBudgetHours * consultantRate,
        actualCost: consultantActualHours * consultantRate,
        monthlyHours: distributeHoursAcrossMonths(consultantBudgetHours),
      },
      {
        id: 503,
        role: 'Developer',
        budgetHours: developerBudgetHours,
        allocatedHours: 0, // Will be calculated from team members
        actualHours: developerActualHours,
        remainingHours: developerBudgetHours - developerActualHours,
        percentUsed: (developerActualHours / developerBudgetHours) * 100,
        billRate: developerRate,
        budgetCost: developerBudgetHours * developerRate,
        actualCost: developerActualHours * developerRate,
        monthlyHours: distributeHoursAcrossMonths(developerBudgetHours),
      },
      {
        id: 504,
        role: 'QA Analyst',
        budgetHours: qaBudgetHours,
        allocatedHours: 0, // Will be calculated from team members
        actualHours: qaActualHours,
        remainingHours: qaBudgetHours - qaActualHours,
        percentUsed: (qaActualHours / qaBudgetHours) * 100,
        billRate: qaRate,
        budgetCost: qaBudgetHours * qaRate,
        actualCost: qaActualHours * qaRate,
        monthlyHours: distributeHoursAcrossMonths(qaBudgetHours),
      },
    ],
    byPerson: [
      {
        id: 601,
        personId: 1,
        personName: 'Sarah Johnson',
        role: 'Project Manager',
        budgetHours: pmBudgetHours,
        actualHours: pmActualHours,
        utilisation: (pmActualHours / pmBudgetHours) * 100,
        costRate: pmRate,
        budgetCost: pmBudgetHours * pmRate,
        actualCost: pmActualHours * pmRate,
      },
      {
        id: 602,
        personId: 2,
        personName: 'Michael Chen',
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours * 0.6,
        actualHours: consultantActualHours * 0.6,
        utilisation: (consultantActualHours * 0.6 / (consultantBudgetHours * 0.6)) * 100,
        costRate: consultantRate,
        budgetCost: consultantBudgetHours * 0.6 * consultantRate,
        actualCost: consultantActualHours * 0.6 * consultantRate,
      },
      {
        id: 603,
        personId: 3,
        personName: 'Emily Rodriguez',
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours * 0.4,
        actualHours: consultantActualHours * 0.4,
        utilisation: (consultantActualHours * 0.4 / (consultantBudgetHours * 0.4)) * 100,
        costRate: consultantRate,
        budgetCost: consultantBudgetHours * 0.4 * consultantRate,
        actualCost: consultantActualHours * 0.4 * consultantRate,
      },
      {
        id: 604,
        personId: 4,
        personName: 'David Kim',
        role: 'Developer',
        budgetHours: developerBudgetHours * 0.5,
        actualHours: developerActualHours * 0.5,
        utilisation: (developerActualHours * 0.5 / (developerBudgetHours * 0.5)) * 100,
        costRate: developerRate,
        budgetCost: developerBudgetHours * 0.5 * developerRate,
        actualCost: developerActualHours * 0.5 * developerRate,
      },
      {
        id: 605,
        personId: 5,
        personName: 'Jessica Taylor',
        role: 'Developer',
        budgetHours: developerBudgetHours * 0.5,
        actualHours: developerActualHours * 0.5,
        utilisation: (developerActualHours * 0.5 / (developerBudgetHours * 0.5)) * 100,
        costRate: developerRate,
        budgetCost: developerBudgetHours * 0.5 * developerRate,
        actualCost: developerActualHours * 0.5 * developerRate,
      },
      {
        id: 606,
        personId: 6,
        personName: 'Alex Martinez',
        role: 'QA Analyst',
        budgetHours: qaBudgetHours,
        actualHours: qaActualHours,
        utilisation: (qaActualHours / qaBudgetHours) * 100,
        costRate: qaRate,
        budgetCost: qaBudgetHours * qaRate,
        actualCost: qaActualHours * qaRate,
      },
    ],
    byPhase: [
      {
        phaseId: 1,
        phaseName: 'Discovery & Planning',
        budgetHours: project.budgetHours * 0.15,
        actualHours: project.actualHours * 0.18,
        remainingHours: Math.max(0, project.budgetHours * 0.15 - project.actualHours * 0.18),
        status: 'complete',
        startDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 2,
        phaseName: 'Design',
        budgetHours: project.budgetHours * 0.20,
        actualHours: project.actualHours * 0.22,
        remainingHours: Math.max(0, project.budgetHours * 0.20 - project.actualHours * 0.22),
        status: 'complete',
        startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 3,
        phaseName: 'Development',
        budgetHours: project.budgetHours * 0.45,
        actualHours: project.actualHours * 0.42,
        remainingHours: Math.max(0, project.budgetHours * 0.45 - project.actualHours * 0.42),
        status: 'in-progress',
        startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 4,
        phaseName: 'Testing & QA',
        budgetHours: project.budgetHours * 0.15,
        actualHours: project.actualHours * 0.12,
        remainingHours: Math.max(0, project.budgetHours * 0.15 - project.actualHours * 0.12),
        status: 'in-progress',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 5,
        phaseName: 'Deployment & Handover',
        budgetHours: project.budgetHours * 0.05,
        actualHours: 0,
        remainingHours: project.budgetHours * 0.05,
        status: 'not-started',
        startDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
    ],
  };
  
  // Generate burn rate data (must be before allBudgetsView)
  const monthsElapsed = Math.max(1, Math.floor(progress * 12));
  const burnRateData = generateBurnRateData(totalBudget, totalActual, progress, monthsElapsed);
  
  // Generate forecast (must be before allBudgetsView)
  const projectedFinalCost = progress > 0 ? totalActual / progress : totalBudget;
  const projectedFinalRevenue = progress > 0 ? project.actualRevenue / progress : project.budgetRevenue;
  const projectedFinalMargin = projectedFinalRevenue > 0 ? ((projectedFinalRevenue - projectedFinalCost) / projectedFinalRevenue) * 100 : 0;
  
  // Generate all budgets view
  const allBudgetsView: AllBudgetsView = {
    projectId: project.id,
    profitability: {
      revenue: {
        budget: project.budgetRevenue,
        actual: project.actualRevenue,
        variance: project.actualRevenue - project.budgetRevenue,
      },
      directCosts: {
        budget: totalBudget,
        actual: totalActual,
        variance: totalActual - totalBudget,
      },
      grossProfit: {
        budget: project.budgetRevenue - totalBudget,
        actual: project.actualRevenue - totalActual,
        variance: (project.actualRevenue - totalActual) - (project.budgetRevenue - totalBudget),
      },
      grossMargin: {
        budget: budgetedMargin,
        actual: actualMargin,
        variance: marginVariance,
      },
      overhead: {
        budget: 0,
        actual: 0,
        variance: 0,
      },
      netProfit: {
        budget: (project.budgetRevenue - totalBudget) * 0.95,
        actual: (project.actualRevenue - totalActual) * 0.95,
        variance: (project.actualRevenue - totalActual) * 0.95 - (project.budgetRevenue - totalBudget) * 0.95,
      },
      netMargin: {
        budget: budgetedMargin * 0.95,
        actual: actualMargin * 0.95,
        variance: (actualMargin - budgetedMargin) * 0.95,
      },
    },
    burnRateData,
    forecast: {
      projectedFinalRevenue,
      projectedFinalCost,
      projectedFinalMargin,
      revenueVariance: projectedFinalRevenue - project.budgetRevenue,
      costVariance: projectedFinalCost - totalBudget,
      marginVariance: projectedFinalMargin - budgetedMargin,
      warnings: [],
    },
  };
  
  // Generate alerts based on project health
  const alerts: BudgetAlert[] = [];
  if (project.health === 'red') {
    alerts.push({
      id: project.id * 1000 + 1,
      projectId: project.id,
      type: 'cost-over-budget',
      severity: 'critical',
      title: 'Cost Budget Exceeded',
      description: `Cost budget significantly exceeded - ${((totalActual / totalBudget - 1) * 100).toFixed(1)}% over budget`,
      message: `Cost budget significantly exceeded - ${((totalActual / totalBudget - 1) * 100).toFixed(1)}% over budget`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
    alerts.push({
      id: project.id * 1000 + 2,
      projectId: project.id,
      type: 'margin-erosion',
      severity: 'warning',
      title: 'Margin Decline',
      description: `Margin has declined from ${budgetedMargin.toFixed(1)}% to ${actualMargin.toFixed(1)}%`,
      message: `Margin has declined from ${budgetedMargin.toFixed(1)}% to ${actualMargin.toFixed(1)}%`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
  } else if (project.health === 'amber') {
    alerts.push({
      id: project.id * 1000 + 1,
      projectId: project.id,
      type: 'cost-warning',
      severity: 'warning',
      title: 'Cost Warning',
      description: `Cost tracking ${((totalActual / totalBudget) * 100).toFixed(1)}% of budget with ${project.completionPercent}% completion`,
      message: `Cost tracking ${((totalActual / totalBudget) * 100).toFixed(1)}% of budget with ${project.completionPercent}% completion`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
  }
  
  // Generate budget revisions
  const revisions: BudgetRevision[] = [
    {
      id: project.id * 100 + 1,
      projectId: project.id,
      revisionNumber: 1,
      date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial budget approval',
      status: 'approved',
      costBudget: totalBudget,
      salesBudget: project.budgetRevenue,
      resourceBudget: project.budgetHours,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: new Date(Date.now() - 89 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    },
  ];
  
  if (project.health === 'amber' || project.health === 'red') {
    revisions.push({
      id: project.id * 100 + 2,
      projectId: project.id,
      revisionNumber: 2,
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      changedBy: 'e2',
      changedByName: 'Bob Smith',
      reason: 'Budget adjustment due to scope changes',
      status: 'current',
      costBudget: totalBudget * 1.1,
      salesBudget: project.budgetRevenue * 1.05,
      resourceBudget: project.budgetHours * 1.08,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
  }
  
  // Build summary
  const summary: BudgetSummary = {
    revenue: {
      budget: project.budgetRevenue,
      actual: project.actualRevenue,
      variance: project.actualRevenue - project.budgetRevenue,
      variancePercent: ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100,
      progressPercent: (project.actualRevenue / project.budgetRevenue) * 100,
      trend: project.actualRevenue > project.budgetRevenue * progress ? 'up' : 'down',
      status: getStatus(project.health, Math.abs((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100),
    },
    costs: {
      budget: totalBudget,
      actual: totalActual,
      variance: totalBudget - totalActual,
      variancePercent: ((totalBudget - totalActual) / totalBudget) * 100,
      progressPercent: (totalActual / totalBudget) * 100,
      trend: totalActual > totalBudget * progress ? 'up' : 'down',
      status: getStatus(project.health, Math.abs((totalActual - totalBudget * progress) / totalBudget) * 100),
    },
    margin: {
      budgetedMargin,
      actualMargin,
      variance: marginVariance,
      status: getStatus(project.health, Math.abs(marginVariance)),
    },
    hours: {
      budget: project.budgetHours,
      actual: project.actualHours,
      remaining: project.budgetHours - project.actualHours,
      progressPercent: (project.actualHours / project.budgetHours) * 100,
      status: getStatus(project.health, Math.abs((project.actualHours / project.budgetHours - progress) * 100)),
    },
    health: {
      score: project.health,
      statusText: project.health === 'green' ? 'On Track' : project.health === 'amber' ? 'Requires Attention' : 'Critical',
      factors: alerts.map(a => a.message),
    },
  };
  
  return {
    projectId: project.id,
    projectName: project.name,
    projectType: project.type,
    summary,
    costBudget,
    salesBudget,
    resourceBudget,
    allBudgets: allBudgetsView,
    alerts,
    revisions,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export const mockBudgetData: Record<string, ProjectBudgetData> = {
  p1: fixedPriceProject,
  p2: timeAndMaterialsProject,
  p3: retainerProject,
  p4: legalCaseManagementProject,
};

export function getBudgetDataByProjectId(projectId: number): ProjectBudgetData | undefined {
  // If we have specific mock data for this project, return it
  const projectIdStr = String(projectId);
  if (mockBudgetData[projectIdStr]) {
    return mockBudgetData[projectIdStr];
  }
  
  // Find the project in mockProjects
  const project = mockProjects.find(p => p.id === projectId);
  if (!project) {
    return undefined;
  }
  
  // Generate budget data dynamically
  return generateBudgetForProject(project);
}

export function getAllBudgetData(): ProjectBudgetData[] {
  // Return all projects with budget data
  return mockProjects.map(project => {
    // Use existing mock data if available
    if (mockBudgetData[project.id]) {
      return mockBudgetData[project.id];
    }
    // Otherwise generate it
    return generateBudgetForProject(project);
  });
}
